import { alpha, Stack, Theme } from '@mui/material';
import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import Icon from '../icon';
import Text from '../text';
import { createUseStyles } from 'react-jss';
import { IconType } from '../icon/helpers';

interface Props {
  onClick: () => void;
  label?: string;
  icon?: IconType;
  image?: ReactNode;
  active?: boolean;
  border?: boolean;
  primary?: boolean;
  hideLabel?: boolean;
  className?: string;
}

const useStyles = createUseStyles<string, { width: number }>((theme: Theme) => ({
  linkBarItem: {
    cursor: 'pointer',
    height: 60,
    boxSizing: 'border-box',
    padding: '10px 10px 10px 16px',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderTop: '1px solid transparent',
    transition: theme.transitions.create(['background-color', 'color'], { duration: 150 }),
    '& svg': {
      color: 'currentColor',
    },
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.sidebar, 0.2)
          : alpha(theme.palette.common.black, 0.06),
    },
    '&.active': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.sidebar, 0.3)
          : alpha(theme.palette.common.black, 0.09),
      '&.primary': {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.sidebar, 0.5)
            : alpha(theme.palette.common.black, 0.12),
      },
    },
    '&.border': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '&.hide-label': {
      justifyContent: 'center',
      padding: 10,
    },
  },
}));

const LinkBarItem: FC<Props> = ({
  onClick,
  label = '',
  icon,
  image: ImageEl,
  active = false,
  border = false,
  primary = false,
  hideLabel = false,
  className = '',
}) => {
  const styles = useStyles();
  const compClass = classNames(styles.linkBarItem, className, {
    active,
    border,
    primary,
    'hide-label': hideLabel,
  });
  const hasLabel = !!label;
  const hasIcon = !!icon;
  const hasImage = !!ImageEl;

  return (
    <Stack
      gap={1}
      direction="row"
      width="100%"
      role="button"
      onClick={onClick}
      className={compClass}
    >
      {hasImage && ImageEl}
      {hasIcon && <Icon name={icon} />}
      {hasLabel && !hideLabel && (
        <Text size="small" dotdot>
          {label}
        </Text>
      )}
    </Stack>
  );
};

export default LinkBarItem;
